import { useCallback, useEffect, useState } from "react";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";

import {
  getSettingHistory,
  messageForError,
  stateForError,
} from "../../../services/notificationSettings";
import { PanelState, SettingChange } from "../../../types/notificationSettings";
import { formatInstant } from "../../../utils/instantFormat";

const ACTOR: Record<string, { label: string; severity: "danger" | "info" | "warning" }> = {
  ADMIN: { label: "changed by staff", severity: "danger" },
  SELF: { label: "changed by the person", severity: "info" },
  UNSUBSCRIBE_LINK: { label: "unsubscribe link", severity: "warning" },
};

/** Only the keys that actually moved, so the entry reads as a change rather than a dump. */
const diff = (change: SettingChange): [string, string, string][] =>
  Array.from(new Set([...Object.keys(change.before), ...Object.keys(change.after)]))
    .filter((k) => (change.before[k] ?? "") !== (change.after[k] ?? ""))
    .map((k) => [k, change.before[k] ?? "", change.after[k] ?? ""]);

const SettingChangeHistory = ({ userId }: { userId: string }) => {
  const [state, setState] = useState<PanelState>("loading");
  const [changes, setChanges] = useState<SettingChange[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const history = await getSettingHistory(userId);
      setChanges(history);
      setState(history.length ? "ready" : "empty");
    } catch (e) {
      setError(messageForError(e));
      setState(stateForError(e));
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") {
    return (
      <div className="flex align-items-center gap-3 py-4" data-testid="history-loading">
        <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="4" />
        <span className="text-color-secondary">Loading history…</span>
      </div>
    );
  }

  if (state === "refused") {
    return (
      <div className="py-3" data-testid="history-refused">
        <Message severity="warn" text="Viewing this history requires an administrator entitlement." />
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="py-3" data-testid="history-failed">
        <Message severity="error" text={`Could not load the history${error ? `: ${error}` : ""}`} />
      </div>
    );
  }

  if (state === "empty") {
    return (
      <div className="py-3" data-testid="history-empty">
        <Message severity="info" text="No changes recorded for this person." />
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3" data-testid="history-ready">
      {changes.map((change) => {
        const actor = ACTOR[change.actorKind];
        return (
          <div key={change.changeId} data-testid={`change-${change.changeId}`} className="flex flex-column gap-1 pb-2 border-bottom-1 surface-border">
            <div className="flex align-items-center gap-2 flex-wrap">
              <Tag
                data-testid={`actor-${change.actorKind}`}
                severity={actor?.severity ?? "info"}
                value={actor?.label ?? change.actorKind}
              />
              <span className="font-medium">{change.actorEmail || change.actor}</span>
              {change.actorEmail && (
                <span className="text-xs text-color-secondary" title="token subject">
                  {change.actor}
                </span>
              )}
              <span className="text-color-secondary text-sm">{formatInstant(change.changedAt)}</span>
            </div>
            <div className="text-sm">
              {diff(change).map(([key, before, after]) => (
                <div key={key}>
                  <span className="text-color-secondary">{key}: </span>
                  <span>{before || "—"}</span>
                  <span className="text-color-secondary"> → </span>
                  <span className="font-medium">{after || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SettingChangeHistory;
